import {useMemo} from 'react';
import {assert} from 'assert-ts';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {useAppSelector} from 'store/hooks';
import {getLinkedRoleCodelist} from 'schemas/links/functions';
import type {CodeListId, CodeListMap} from '../types';
import {selectCodelists} from '../codelistsSlice';
import {useRestrictedCodelistsContext} from '../contexts';
import {getDummyCodeList} from './getDummyCodeList';

export const useOptionalCodeLists = (
  ids: (CodeListId | LinkedRoleCodeListId)[] | undefined,
): CodeListMap | undefined => {
  const localization = useLocalization();
  const {getRestrictedCodelist} = useRestrictedCodelistsContext();
  const codeListState = useAppSelector(selectCodelists);
  return useMemo(() => {
    if (!ids) {
      return undefined;
    }

    const map = ids.reduce<CodeListMap>((acc, id) => {
      if (isLinkedRoleCodeListId(id)) {
        const codelist = getLinkedRoleCodelist(id, localization);
        acc[id] = codelist;
        return acc;
      }

      const codeList = codeListState[id]?.data;
      if (
        assert.soft(codeList, 'useOptionalCodeLists: Code list not loaded', {
          id,
        })
      ) {
        acc[id] = getRestrictedCodelist(codeList);
      } else {
        acc[id] = getDummyCodeList(id);
      }

      return acc;
    }, {});

    return map;
  }, [codeListState, getRestrictedCodelist, ids, localization]);
};
